// Common zone abbreviations shown next to the offset for quick recognition.
const ZONE_NAMES: Record<number, string> = {
  [-8]: 'PST', [-7]: 'PDT', [-6]: 'CST', [-5]: 'EST', [-4]: 'EDT',
  0: 'UTC', 1: 'CET', 2: 'CEST', 3: 'IDT',
};

/**
 * Report timezone the UI opens on, as a fixed UTC offset in hours.
 *
 * Shared by every page with a Timezone selector (tracking, clicks, conversions,
 * audit) so a report row and the clicks you drill into from it cover the same
 * window — differing defaults would silently shift the range between views.
 *
 * Note this is a FIXED offset, not a DST-aware zone: -7 is Pacific in summer
 * (PDT), but Pacific moves to -8 (PST) in winter, so this needs revisiting at
 * the DST boundary or the reporting day drifts an hour.
 */
export const DEFAULT_TZ_OFFSET = -7;

/** The only zones we actually run in — surfaced first so they're one click away. */
export const COMMON_TZ_OFFSETS = [0, -4, -7];

function buildOption(offset: number, group: string) {
  const name = ZONE_NAMES[offset];
  const label = offset === 0
    ? 'UTC'
    : `UTC${offset > 0 ? '+' : ''}${offset}${name ? ` · ${name}` : ''}`;
  return { value: offset, label, group };
}

/**
 * Fixed-offset timezone options (UTC-12 .. UTC+12), shared by every page with a
 * Timezone selector so they stay identical.
 *
 * The three we use are pinned to the top; the rest stay available rather than
 * being removed. Dropping them would silently repoint any existing bookmarked or
 * shared report URL carrying one (?tz=2) at the default — the link would still
 * load, just over a different window, which reads as changed data rather than an
 * error. Autocomplete's groupBy needs options pre-sorted by group, hence the order.
 */
export const TIMEZONE_OPTIONS = [
  ...COMMON_TZ_OFFSETS.map((o) => buildOption(o, 'Commonly used')),
  ...Array.from({ length: 25 }, (_, i) => i - 12)
    .filter((o) => !COMMON_TZ_OFFSETS.includes(o))
    .map((o) => buildOption(o, 'All zones')),
];

/**
 * Parse a stored timestamp. The API marks them, but the same values were bare before
 * the server did, and a bare one is read as local — right on a UTC host, silently
 * wrong anywhere else. Absent a marker, assume UTC.
 */
function parseUtc(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const raw = String(value);
  const marked = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(raw);
  const d = new Date(raw.replace(' ', 'T') + (marked ? '' : 'Z'));
  return isNaN(d.getTime()) ? null : d;
}

/** An instant's UTC parts state the wall clock at UTC; its local parts, the reader's. */
function stamp(d: Date, utc: boolean): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const [Y, Mo, D, h, mi, sec] = utc
    ? [d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()]
    : [d.getFullYear(), d.getMonth() + 1, d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()];
  return `${Y}-${p(Mo)}-${p(D)} ${p(h)}:${p(mi)}:${p(sec)}`;
}

/** Unparseable input is handed back rather than hidden; empty reads as a blank cell. */
function unformatted(value: unknown): string {
  return value === null || value === undefined || value === '' ? '-' : String(value);
}

/** The stored instant as the wall clock at a fixed offset. Literal, so not DST-aware. */
export function formatInOffset(value: string | number | Date | null | undefined, offsetHours: number): string {
  const d = parseUtc(value);
  return d ? stamp(new Date(d.getTime() + offsetHours * 3600_000), true) : unformatted(value);
}

/** The stored instant as the reader's wall clock, via the platform zone database, so DST-correct. */
export function formatLocal(value: string | number | Date | null | undefined): string {
  const d = parseUtc(value);
  return d ? stamp(d, false) : unformatted(value);
}

/** `YYYY-MM-DD HH:MM:SS` at UTC. Pair with a report timezone of 0 (no shift) for an instant range. */
export function utcStamp(d: Date): string {
  return stamp(d, true);
}

/** A report request's day range: from-day 00:00:00 → to-day 23:59:59, local wall-clock (server
 *  shifts by the report timezone). The one place this spelling lives, so every page matches. */
export function dayBounds(from: string, to: string): { date_from: string; date_to: string } {
  return { date_from: `${from} 00:00:00`, date_to: `${to} 23:59:59` };
}

/** dayBounds straight off a date-range picker value; either bound absent falls back to today. */
export function dayRangeParams(range: [Date | null, Date | null]): { date_from: string; date_to: string } {
  const day = (d: Date) => stamp(d, false).slice(0, 10);
  return dayBounds(day(range[0] ?? new Date()), day(range[1] ?? new Date()));
}

export function offsetLabel(offsetHours: number): string {
  return TIMEZONE_OPTIONS.find((o) => o.value === offsetHours)?.label
    ?? `UTC${offsetHours > 0 ? '+' : ''}${offsetHours}`;
}

/** Read per call, not cached: a tab left open across a DST boundary would keep the old zone. */
export function localOffsetHours(): number {
  return -new Date().getTimezoneOffset() / 60;
}

export function localZoneLabel(): string {
  const iana = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const off = localOffsetHours();
  const bare = `UTC${off > 0 ? '+' : ''}${off}`;
  return iana ? `${iana} · ${bare}` : bare;
}
